import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dicomErrorService } from '../services/dataService';
import {
  categoryHint, categoryLabel, DICOM_ERROR_CATEGORIES, DICOM_ERROR_SOURCES, sourceLabel,
} from '../utils/dicomErrors';
import { formatDateTime, StatusPill } from './DetailDashboard';
import Pagination from './Pagination';
import styles from '../pages/assets/css/DetailDashboard.module.css';

const STATUS_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'all', label: 'All' },
  { value: 'acknowledged', label: 'Acknowledged' },
];

/**
 * DICOM errors (refusals, failed pings, filtered-out worklists, MPPS problems).
 * With `equipmentId`: that machine's errors. Without: all errors with extra filters.
 */
const DicomErrorsTable = ({ equipmentId, refreshKey = 0, canAcknowledge = false, pageSize = 10, title = 'DICOM Errors' }) => {
  const isGlobal = !equipmentId;
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: 'open', category: '', severity: '', source: '', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [acknowledging, setAcknowledging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = {
      page,
      page_size: pageSize,
      status: filters.status,
      category: filters.category || undefined,
    };
    try {
      setData(isGlobal
        ? await dicomErrorService.list({
          ...params,
          severity: filters.severity || undefined,
          source: filters.source || undefined,
          search: filters.search || undefined,
        })
        : await dicomErrorService.listForEquipment(equipmentId, params));
    } catch (err) {
      setError(err?.detail || 'Failed to load DICOM errors');
    } finally {
      setLoading(false);
    }
  }, [equipmentId, filters, isGlobal, page, pageSize]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const handleAcknowledge = async (id) => {
    setAcknowledging(id);
    try {
      await dicomErrorService.acknowledge(id);
      await load();
    } catch (err) {
      setError(err?.detail || 'Failed to acknowledge');
    } finally {
      setAcknowledging(null);
    }
  };

  const columnCount = (isGlobal ? 6 : 5) + (canAcknowledge ? 1 : 0);

  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <h2>{title}</h2>
        <div className={styles.filterRow}>
          {isGlobal ? (
            <form
              onSubmit={(e) => { e.preventDefault(); setFilter('search', searchInput.trim()); }}
              className={styles.filterRow}
            >
              <input
                className={styles.filterInput}
                placeholder="AE title, IP or message"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </form>
          ) : null}
          <select className={styles.filterInput} value={filters.category} onChange={(e) => setFilter('category', e.target.value)} aria-label="Category">
            <option value="">All categories</option>
            {Object.entries(DICOM_ERROR_CATEGORIES).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
          </select>
          {isGlobal ? (
            <>
              <select className={styles.filterInput} value={filters.severity} onChange={(e) => setFilter('severity', e.target.value)} aria-label="Severity">
                <option value="">All severities</option>
                <option value="error">Errors</option>
                <option value="warning">Warnings</option>
              </select>
              <select className={styles.filterInput} value={filters.source} onChange={(e) => setFilter('source', e.target.value)} aria-label="Source">
                <option value="">All sources</option>
                {Object.entries(DICOM_ERROR_SOURCES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </>
          ) : null}
          <select className={styles.filterInput} value={filters.status} onChange={(e) => setFilter('status', e.target.value)} aria-label="Status">
            {STATUS_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={`${styles.tableWrap} ${styles.tableFramed}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Last seen</th>
              {isGlobal ? <th>Equipment</th> : null}
              <th>Machine</th>
              <th>Problem</th>
              <th>Details</th>
              <th>Times</th>
              {canAcknowledge ? <th aria-label="Actions" /> : null}
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.id}>
                <td className={styles.nowrap}>
                  {formatDateTime(item.last_occurred_at)}
                  {item.occurrence_count > 1 ? (
                    <span className={styles.subText}>since {formatDateTime(item.first_occurred_at)}</span>
                  ) : null}
                </td>
                {isGlobal ? (
                  <td>
                    {item.equipment_id
                      ? <Link to={`/equipment/${item.equipment_id}`}>{item.equipment_name || item.equipment_asset_id}</Link>
                      : <span className={styles.muted}>Unlinked</span>}
                    {item.equipment_asset_id ? <span className={styles.subText}>{item.equipment_asset_id}</span> : null}
                  </td>
                ) : null}
                <td>
                  <code>{item.ae_title || '—'}</code>
                  <span className={styles.subText}>{item.ip_addr || '—'}</span>
                </td>
                <td>
                  <StatusPill value={item.severity === 'warning' ? 'pending' : 'failed'} label={categoryLabel(item.category)} />
                  <span className={styles.subText}>{sourceLabel(item.source)}</span>
                  {item.acknowledged_at ? (
                    <span className={styles.subText}>Acknowledged by {item.acknowledged_by || '—'}</span>
                  ) : null}
                </td>
                <td className={styles.errorCell}>
                  <span>{item.message}</span>
                  {categoryHint(item.category) ? (
                    <button type="button" className={styles.linkButton} onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                      {expanded === item.id ? 'Hide help' : 'What to do'}
                    </button>
                  ) : null}
                  {expanded === item.id ? <span className={styles.hintText}>{categoryHint(item.category)}</span> : null}
                </td>
                <td>{item.occurrence_count}</td>
                {canAcknowledge ? (
                  <td>
                    {!item.acknowledged_at ? (
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={() => handleAcknowledge(item.id)}
                        disabled={acknowledging === item.id}
                      >
                        {acknowledging === item.id ? 'Saving…' : 'Acknowledge'}
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
            {!loading && data.items.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className={styles.muted}>
                  {filters.status === 'open' ? 'No open DICOM errors.' : 'No DICOM errors match these filters.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={pageSize} total={data.total} loading={loading} onPageChange={setPage} />
    </section>
  );
};

export default DicomErrorsTable;
